import { taskParcelsQueryOptions } from "@/hooks/useTaskRelatedFrames";
import { useCoverageBetaStore } from "@/store/useCoverageBetaStore";
import { mergePropagatedAnnotations } from "@/utils/merge-propagated-annotations";
import type { ProductSchema } from "@labeling-tools/api/atlas";
import { useAtlasProducts } from "@labeling-tools/api/hooks";
import { useQuery } from "@tanstack/react-query";
import { uniq } from "lodash";
import { useEffect, useMemo, useRef } from "react";

// A related task's propagation can land after this task opens: quickly when
// lebron says it's already running (labels_pending — served despite its hold),
// slowly in case a related task is submitted while this one is open.
const PENDING_POLL_MS = 3_000;
const IDLE_POLL_MS = 10_000;
const POLL_FOR_MS = 60_000;

interface Args {
  jobId: string;
  taskId: string;
  enabled: boolean;
  /** The store has been seeded from the parcel (the seed effect has run). */
  isSeeded: boolean;
}

/**
 * Pre-fill the task's areas from labels other tasks propagated onto its
 * primary parcel (see lebron mvp.propagation). Pre-filled areas start
 * unreviewed, so the labeler confirms or fixes each before submitting.
 */
export const usePropagatedPrefill = ({
  jobId,
  taskId,
  enabled,
  isSeeded,
}: Args) => {
  const openedAt = useRef(Date.now());
  useEffect(() => {
    openedAt.current = Date.now();
  }, [taskId]);

  const { data: parcels } = useQuery({
    ...taskParcelsQueryOptions(jobId, taskId),
    enabled: enabled && !!jobId && !!taskId,
    refetchInterval: (query) => {
      if (Date.now() - openedAt.current > POLL_FOR_MS) return false;
      const primary = query.state.data?.find((p) => p.role === "PRIMARY");
      return primary?.labels_pending ? PENDING_POLL_MS : IDLE_POLL_MS;
    },
  });

  // Ignored when disabled: a disabled query still returns what the next-task
  // prefetch cached.
  const annotations = useMemo(
    () =>
      (enabled && parcels?.find((p) => p.role === "PRIMARY")?.annotations) ||
      [],
    [enabled, parcels],
  );
  const productIds = useMemo(
    () => uniq(annotations.flatMap((a) => a.product_ids)),
    [annotations],
  );
  const { products, isLoading: productsLoading } = useAtlasProducts(productIds);
  const basePlanogramId = useCoverageBetaStore(
    (s) => s.basePlanogramIds[0] ?? "",
  );

  useEffect(() => {
    if (!isSeeded || productsLoading || annotations.length === 0) return;
    const store = useCoverageBetaStore.getState();
    if (store.isReadOnly) return;

    const productsById = new Map<string, ProductSchema>();
    products.forEach((p) => p?.id && productsById.set(p.id, p));
    const merged = mergePropagatedAnnotations(
      store.idxToFeEmptyArea,
      annotations,
      productsById,
      basePlanogramId,
    );
    if (!merged) return;
    store.setIdxToFeEmptyArea(merged.idxToFeEmptyArea);
    // New areas have no aisle from the model → aisle 0, like a drawn box.
    merged.addedIdxs.forEach((idx) => store.assignBoxToSlot(idx, 0));
    // `products` is a fresh array each render; productsLoading gates it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSeeded, productsLoading, annotations, basePlanogramId]);
};
